import React, { useState } from 'react';
import type { ReactNode } from 'react';
import { format_duration_minutes } from '../../util_components/instant_alert/instant_timer';
import { DEFAULT_LEAD_MINUTES, has_lead_minutes, validate_lead_minutes } from './gen_config';
import type { GenRule, TierField } from './gen_config';
import RuleTierControls from './RuleTierControls';
import {
  BLANK_EXPIRY_MINUTES,
  EXPIRY_PLACEHOLDER,
  EXPIRY_TITLE,
  delay_label,
  delay_title,
  enabled_since_label,
  has_dedup_window,
  rule_timing_summary,
  rule_trigger_text
} from './rule_card_labels';
import type { CardRule } from './rule_card_labels';
import { Btn, Chip, FIELD_CLASS, LABEL_CLASS, Switch, TOUCH_CLASS, fieldStyle } from './ui';

// Server-supplied bounds for every per-rule number box.
export interface RuleBounds {
  min_refresh_hours: number;
  max_refresh_hours: number;
  min_due_offset_hours: number;
  max_due_offset_hours: number;
  min_expiry_minutes: number;
  max_expiry_minutes: number;
  min_dedup_hours: number;
  max_dedup_hours: number;
  min_grace_minutes: number;
  max_grace_minutes: number;
  min_delay_minutes: number;
  max_delay_minutes: number;
  min_lookback_hours: number;
  max_lookback_hours: number;
  min_lead_minutes: number;
  max_lead_minutes: number;
}

type NumField =
  'expiry_minutes' | 'dedup_window_hours' | 'grace_minutes' | 'delay_minutes' | 'lookback_hours';
type HourField = 'refresh_rate_hours' | 'due_offset_hours';

interface Props {
  rule: CardRule;
  levels: string[] | null | undefined;
  priorities: string[] | null | undefined;
  // Saving, or the caller cannot edit: every value stays visible but inert.
  locked: boolean;
  min_day: number;
  max_day: number;
  bounds: RuleBounds;
  on_set: (patch: Partial<GenRule>) => void;
  on_tier: (field: TierField, value: string) => void;
  on_lead: (raw: string) => void;
  on_optional: (field: NumField, raw: string, min: number, max: number) => void;
  on_hours: (field: HourField, raw: string, min: number, max: number) => void;
  on_toggle_day: (day: number) => void;
  on_toggle_all_days: (select_all: boolean) => void;
}

// One grid for the header and every row: stacked on phones/tablets, a table from lg up.
const RULE_GRID =
  'grid grid-cols-[32px_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 gap-y-2 lg:grid-cols-[44px_minmax(0,2fr)_120px_104px_minmax(0,1.3fr)_64px] lg:items-center';

const CELL_LABEL = 'text-[11.5px] text-content-muted lg:sr-only';
const FIELD = 'inline-flex items-center gap-1.5 text-[12.5px] text-content-muted';

export function RulesHeader() {
  return (
    <div className={`${RULE_GRID} hidden border-b border-line-subtle px-3.5 py-2 lg:grid`}>
      <span className={LABEL_CLASS}>On</span>
      <span className={LABEL_CLASS}>Rule · trigger</span>
      <span className={LABEL_CLASS}>Level</span>
      <span className={LABEL_CLASS}>Priority</span>
      <span className={LABEL_CLASS}>Timing</span>
      <span />
    </div>
  );
}

const blank_to_empty = (v: unknown) => (v === '' || v === null || v === undefined ? '' : v);

function NumberBox({
  label,
  suffix,
  title,
  value,
  min,
  max,
  disabled,
  placeholder = '',
  on_change
}: {
  label: ReactNode;
  suffix?: ReactNode;
  title?: string;
  value: unknown;
  min: number;
  max: number;
  disabled: boolean;
  placeholder?: string;
  on_change: (raw: string) => void;
}) {
  return (
    <label className={FIELD} style={{ opacity: disabled ? 0.5 : 1 }} title={title}>
      {label}
      <input
        type="number"
        disabled={disabled}
        min={min}
        max={max}
        value={value as string | number}
        placeholder={placeholder}
        onChange={(e) => on_change(e.target.value)}
        className={FIELD_CLASS}
        style={fieldStyle(64)}
      />
      {suffix}
    </label>
  );
}

// One generation rule: a summary row, plus the existing per-rule fields behind Edit.
function RuleRow({
  rule,
  levels,
  priorities,
  locked,
  min_day,
  max_day,
  bounds: b,
  on_set,
  on_tier,
  on_lead,
  on_optional,
  on_hours,
  on_toggle_day,
  on_toggle_all_days
}: Props) {
  const [expanded, set_expanded] = useState(false);
  const disabled = locked || !rule.enabled;
  const label = rule.label || rule.task_type;
  const lead_error = validate_lead_minutes(rule, {
    min: b.min_lead_minutes,
    max: b.max_lead_minutes
  });
  // A lead error blocks Save, so its field never folds away while it is wrong.
  const open = expanded || !!lead_error;
  const panel_id = `rule-panel-${rule.task_type}`;
  const trigger = rule_trigger_text(rule);
  const since = enabled_since_label(rule.enabled_at);
  // A rule whose generator never reads rule.days says so with has_days:false; absent means true.
  const has_days = !rule.enabled_only && rule.has_days !== false;
  const all_days: number[] = [];
  for (let d = min_day; d <= max_day; d += 1) all_days.push(d);
  const selected = rule.days || [];

  // The server sends 0 for "no expiry"; the box is blank in both cases.
  const expiry = rule.expiry_minutes ? rule.expiry_minutes : '';
  // 0 is a real grace and a real dedup window; 0 delay/lookback is below the server minimum.
  const delay = rule.delay_minutes ? rule.delay_minutes : '';
  const lookback = rule.lookback_hours ? rule.lookback_hours : '';
  const lead_id = `rule-lead-${rule.task_type}`;

  return (
    <div
      className={`border-t border-line-subtle first:border-t-0 ${
        open || !rule.enabled ? 'bg-surface-row-muted' : ''
      }`}
    >
      <div className={`${RULE_GRID} px-3.5 py-2.5`}>
        <div className="pt-0.5 lg:pt-0">
          <Switch
            checked={!!rule.enabled}
            disabled={locked}
            label={`${label} enabled`}
            onChange={(enabled) => on_set({ enabled })}
          />
        </div>
        <div className="col-span-2 min-w-0 lg:col-span-1">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-[13px] font-semibold text-content-primary">
            <span className="min-w-0 truncate" title={label}>
              {label}
            </span>
            {rule.is_instant ? <Chip tone="warning">INSTANT</Chip> : null}
            {rule.bucket ? (
              <Chip tone="info" title="Only students currently in this concierge bucket">
                {rule.bucket}
              </Chip>
            ) : null}
          </div>
          {trigger ? <div className="text-[11.5px] text-content-muted">{trigger}</div> : null}
          {since ? <div className="text-[11.5px] text-content-muted">{since}</div> : null}
        </div>
        <RuleTierControls
          rule={rule}
          levels={levels}
          priorities={priorities}
          disabled={locked}
          on_change={on_tier}
        />
        <div className="col-span-2 col-start-2 min-w-0 text-xs text-content-secondary lg:col-span-1 lg:col-start-auto">
          <span className={CELL_LABEL}>Timing: </span>
          {rule_timing_summary(rule)}
          {/* Kept out of the Priority cell so the Level and Priority selects stay on one line. */}
          {rule.no_book_day_zero_override ? (
            <span className="block text-[11.5px] text-content-muted">Day 0 is always P0.</span>
          ) : null}
        </div>
        <div className="col-span-2 col-start-2 lg:col-span-1 lg:col-start-auto lg:justify-self-end">
          <Btn
            size="row"
            aria-expanded={open}
            aria-controls={panel_id}
            aria-label={`${open ? 'Close' : 'Edit'} ${label}`}
            disabled={!!lead_error}
            title={lead_error ? 'Fix the lead time first' : undefined}
            onClick={() => set_expanded(!open)}
            className={`max-lg:!w-full ${TOUCH_CLASS}`}
          >
            {open ? 'Close' : 'Edit'}
          </Btn>
        </div>
      </div>

      {open ? (
        <div
          id={panel_id}
          className="mx-3.5 mb-3 flex flex-col gap-2.5 rounded-lg border border-line-subtle bg-surface-card p-3 lg:ml-[66px]"
        >
          {has_days ? (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs text-content-muted">
                <span>Days {rule.anchor_label}</span>
                <span className="font-semibold text-content-primary">
                  {selected.length ? selected.join(', ') : 'none picked'}
                </span>
                <label
                  className="ml-auto inline-flex items-center gap-1.5 font-semibold text-content-link max-sm:min-h-[40px]"
                  style={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}
                  title="Select or clear every day slot"
                >
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={selected.length === all_days.length && all_days.length > 0}
                    onChange={(e) => on_toggle_all_days(e.target.checked)}
                    style={{ margin: 0 }}
                  />
                  All days
                </label>
              </div>
              <div className="grid grid-cols-[repeat(10,minmax(0,1fr))] gap-1 sm:grid-cols-[repeat(15,minmax(0,1fr))]">
                {all_days.map((d) => {
                  const on = selected.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      aria-pressed={on}
                      disabled={disabled}
                      onClick={() => on_toggle_day(d)}
                      title={`Day ${d} ${rule.anchor_label || ''}`}
                      className={TOUCH_CLASS}
                      style={{
                        margin: 0,
                        height: 28,
                        padding: 0,
                        borderRadius: 6,
                        fontSize: 12,
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: on ? 600 : 400,
                        cursor: disabled ? 'default' : 'pointer',
                        border: `1px solid ${on ? 'var(--brand-primary)' : 'var(--border-subtle)'}`,
                        background: on ? 'var(--brand-primary-soft)' : 'var(--surface-card)',
                        color: on ? 'var(--text-link-brand)' : 'var(--text-secondary)',
                        opacity: disabled ? 0.5 : 1
                      }}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}

          {rule.enabled_only ? null : (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <NumberBox
                label="Refresh every"
                suffix="hours"
                title="How often the generator re-scans this rule for candidates. The cron runs hourly; 0 = every run."
                value={rule.refresh_rate_hours ?? 24}
                min={b.min_refresh_hours}
                max={b.max_refresh_hours}
                disabled={disabled}
                on_change={(raw) =>
                  on_hours('refresh_rate_hours', raw, b.min_refresh_hours, b.max_refresh_hours)
                }
              />
              <NumberBox
                label="Due"
                suffix="hours after generation"
                title="A generated task's due time = the moment it was generated + this many hours."
                value={rule.due_offset_hours ?? 24}
                min={b.min_due_offset_hours}
                max={b.max_due_offset_hours}
                disabled={disabled}
                on_change={(raw) =>
                  on_hours('due_offset_hours', raw, b.min_due_offset_hours, b.max_due_offset_hours)
                }
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <label
              className={`${FIELD} font-medium max-sm:min-h-[40px]`}
              style={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}
              title="Instant tasks sort above every standard task and expire if untouched."
            >
              <input
                type="checkbox"
                disabled={disabled}
                checked={!!rule.is_instant}
                onChange={(e) => on_set({ is_instant: e.target.checked })}
                style={{ margin: 0 }}
              />
              Instant
            </label>
            {rule.is_instant ? (
              <NumberBox
                label="Expiry duration"
                suffix="minutes"
                title={EXPIRY_TITLE}
                placeholder={EXPIRY_PLACEHOLDER}
                value={expiry}
                min={b.min_expiry_minutes}
                max={b.max_expiry_minutes}
                disabled={disabled}
                on_change={(raw) =>
                  on_optional('expiry_minutes', raw, b.min_expiry_minutes, b.max_expiry_minutes)
                }
              />
            ) : null}
            {rule.is_instant && has_dedup_window(rule.task_type) ? (
              <NumberBox
                label="Dedup window"
                suffix="hours"
                title="A second task of this type for the same member is suppressed inside this window. 0 = no window."
                value={blank_to_empty(rule.dedup_window_hours)}
                min={b.min_dedup_hours}
                max={b.max_dedup_hours}
                disabled={disabled}
                on_change={(raw) =>
                  on_optional('dedup_window_hours', raw, b.min_dedup_hours, b.max_dedup_hours)
                }
              />
            ) : null}
            {/* Grace and delay change when a task is created, not its expiry, so they
                follow the server's has_grace / has_delay rather than the Instant toggle. */}
            {rule.has_grace ? (
              <NumberBox
                label="Grace"
                suffix="minutes"
                title="How long to wait after the trigger before creating the task, giving the member a chance to finish on their own. Blank = the server default."
                value={blank_to_empty(rule.grace_minutes)}
                min={b.min_grace_minutes}
                max={b.max_grace_minutes}
                disabled={disabled}
                on_change={(raw) =>
                  on_optional('grace_minutes', raw, b.min_grace_minutes, b.max_grace_minutes)
                }
              />
            ) : null}
            {rule.has_delay ? (
              <NumberBox
                label={delay_label(rule.task_type)}
                title={delay_title(rule.task_type)}
                value={delay}
                min={b.min_delay_minutes}
                max={b.max_delay_minutes}
                disabled={disabled}
                on_change={(raw) =>
                  on_optional('delay_minutes', raw, b.min_delay_minutes, b.max_delay_minutes)
                }
              />
            ) : null}
            {has_lead_minutes(rule) ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                {/* Kept unclamped with an error (which blocks Save), unlike the boxes above. */}
                <label
                  htmlFor={lead_id}
                  className={FIELD}
                  style={{ opacity: disabled ? 0.5 : 1 }}
                  title={`How many minutes before the session starts the joining-call task is created (${b.min_lead_minutes}–${b.max_lead_minutes}).`}
                >
                  Lead time (minutes before session)
                  <input
                    id={lead_id}
                    type="number"
                    disabled={disabled}
                    min={b.min_lead_minutes}
                    max={b.max_lead_minutes}
                    step={1}
                    value={blank_to_empty(rule.lead_minutes) as string | number}
                    placeholder={String(DEFAULT_LEAD_MINUTES)}
                    aria-invalid={lead_error ? true : undefined}
                    aria-describedby={lead_error ? `${lead_id}-error` : undefined}
                    onChange={(e) => on_lead(e.target.value)}
                    className={FIELD_CLASS}
                    style={fieldStyle(64, !!lead_error)}
                  />
                </label>
                {lead_error ? (
                  <span
                    id={`${lead_id}-error`}
                    role="alert"
                    className="text-xs text-state-error-text"
                  >
                    {lead_error}
                  </span>
                ) : null}
              </span>
            ) : null}
            {rule.has_lookback ? (
              <NumberBox
                label="Lookback"
                suffix="hours"
                title="How far before the session end a checkout still counts as started payment. Blank = the server default."
                value={lookback}
                min={b.min_lookback_hours}
                max={b.max_lookback_hours}
                disabled={disabled}
                on_change={(raw) =>
                  on_optional('lookback_hours', raw, b.min_lookback_hours, b.max_lookback_hours)
                }
              />
            ) : null}
          </div>
          {rule.is_instant ? (
            <span className="text-xs text-content-muted" style={{ opacity: disabled ? 0.5 : 1 }}>
              untouched after {format_duration_minutes(expiry || BLANK_EXPIRY_MINUTES)} →
              auto-completed
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default RuleRow;
